import React from "react";
import type { ProfileFindings as ProfileFindingsBlock } from "../../../services/visibilityService";
import { engineLabel, roleTitle } from "./visibilityLabels";
import {
  CleanFinding,
  FindingGroup,
  cleanFinding,
  groupFindings,
  isRecord,
  namesList,
  plural,
  presence,
  serviceKey,
} from "../profileFindingsModel";

export { namesList, safeCitedPage } from "../profileFindingsModel";

/**
 * Business Profile findings in the AI Visibility dashboard's Opportunities
 * card (SGL-328 AC4).
 *
 * One finding type today, `service_not_listed`: a local buyer question the
 * business lost, where AI named others instead and the business's own Google
 * Business Profile does not list the service asked for.
 *
 * Each finding starts from the buyer question, never the profile field — the
 * owner cares that they lost "emergency plumber in Georgetown", and the missing
 * service is the reason, not the headline. The evidence (per-engine outcome,
 * pages AI cited, what we read on the profile, what to change) sits behind a
 * native <details>, collapsed at rest.
 *
 * The backend sends one finding per lost question, with one spelling per
 * service. Findings for the same service are one gap, so they render as ONE
 * card ("affects 10 questions"), not ten cards that each say "affects 1".
 *
 * Honesty rules (same as the rest of the dashboard):
 *   - Every non-"ok" status is "we could not check, and here is why", never
 *     "nothing found". Only "ok" with no findings may say nothing was found.
 *   - No zero is shown as a measurement: a status other than "ok" carries
 *     questions_checked = 0, and that 0 is never rendered.
 *   - "You are not named" only when an engine measurably did not name them;
 *     if any engine named them, the wording says so.
 *   - null/absent (not computed for this run) and not_a_customer render nothing.
 *   - not_connected / needs_reauth render nothing: the Google Business Profile
 *     section below already shows the one Connect / Reconnect prompt.
 *
 * Rendered inside AiVisibilityDashboard's `.avm-scope`, so it uses that design
 * system's tokens (light + dark); its own classes are prefixed `avm-pf-`.
 */

/** Roles that are a measurement; anything else reads "Could not be measured". */
const MEASURED_ROLES = new Set(["recommended", "mentioned", "cited_source_only", "absent"]);


/**
 * The buyer question, who AI named instead, and how the business fared. With
 * several questions, the first (highest-intent) one leads and the rest are
 * counted; how the business fared is judged across all of them, so "not
 * named" is only said when no engine named them for any of the questions.
 */
function leadSentence(g: FindingGroup): string {
  const [first] = g.questions;
  const others = g.questions.length - 1;
  const ask = others > 0
    ? `When someone asks AI “${first.question}” and ${plural(others, "similar question")},`
    : `When someone asks AI “${first.question}”,`;
  const p = presence(g.questions.flatMap((q) => q.outcomes));
  if (g.competitors.length) {
    const you =
      p === "named" ? " You're named but not recommended."
      : p === "source" ? " Your site is used as a source, but you're not named."
      : p === "absent" ? " You are not named."
      : "";
    return `${ask} it names ${namesList(g.competitors)}.${you}`;
  }
  switch (p) {
    case "named": return `${ask} you're named but not recommended.`;
    case "source": return `${ask} your site is used as a source, but you're not named.`;
    case "absent": return `${ask} you are not named.`;
    default: return `${ask} it doesn't recommend you.`;
  }
}

/* ─────────────────────────── styles ────────────────────────────────────── */

const PF_STYLES = `
.avm-scope .avm-pf-list { display: flex; flex-direction: column; gap: 12px; }
.avm-scope .avm-pf-card { border: 1px solid var(--avm-line); border-left: 3px solid var(--avm-warn); border-radius: 10px; padding: 14px 16px; background: var(--avm-surface); overflow-wrap: anywhere; }
.avm-scope .avm-pf-top { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 6px; }
.avm-scope .avm-pf-title { font-size: 15px; }
.avm-scope .avm-pf-impact { font-size: 12px; font-weight: 700; color: var(--avm-warn); background: var(--avm-warn-bg); padding: 2px 9px; border-radius: 999px; white-space: nowrap; }
.avm-scope .avm-pf-lead { margin: 0; font-size: 14px; color: var(--avm-ink); }
.avm-scope .avm-pf-gap { margin: 4px 0 0; font-size: 14px; font-weight: 600; color: var(--avm-ink); }
.avm-scope .avm-pf-why { margin-top: 10px; }
.avm-scope .avm-pf-why summary { cursor: pointer; font-size: 13px; font-weight: 600; color: var(--avm-primary); }
.avm-scope .avm-pf-h { font-size: 12px; font-weight: 700; color: var(--avm-muted); letter-spacing: .03em; margin: 12px 0 4px; }
.avm-scope .avm-pf-ul { list-style: none; margin: 0; padding: 0; font-size: 13px; }
.avm-scope .avm-pf-ul li { padding: 2px 0; overflow-wrap: anywhere; }
.avm-scope .avm-pf-ul a { color: var(--avm-primary); }
.avm-scope .avm-pf-engine { display: inline-block; min-width: 84px; font-weight: 600; }
.avm-scope .avm-pf-muted { color: var(--avm-muted); }
.avm-scope .avm-pf-change { margin: 0; font-size: 13px; color: var(--avm-ink); }
.avm-scope .avm-pf-line { margin: 0; font-size: 14px; color: var(--avm-muted); }
.avm-scope .avm-pf-q { padding: 4px 0; }
.avm-scope .avm-pf-qtext { font-weight: 600; color: var(--avm-ink); }
.avm-scope .avm-pf-sub { margin: 2px 0 0 12px; }
`;

/* ─────────────────────────── pieces ───────────────────────────────────── */

const OutcomeItems: React.FC<{ outcomes: [string, string][] }> = ({ outcomes }) => (
  <>
    {outcomes.map(([engine, role]) => (
      <li key={engine}>
        <span className="avm-pf-engine">{engineLabel(engine)}</span>
        <span className={MEASURED_ROLES.has(role) ? undefined : "avm-pf-muted"}>
          {roleTitle(role)}
        </span>
      </li>
    ))}
  </>
);

const FindingCard: React.FC<{ g: FindingGroup }> = ({ g }) => {
  const single = g.questions.length === 1;
  return (
    <article className="avm-pf-card" aria-label={`Service not listed: ${g.service}`}>
      <div className="avm-pf-top">
        <h3 className="avm-pf-title">Service not listed</h3>
        <span className="avm-pf-impact">affects {plural(g.questions.length, "question")}</span>
      </div>
      <p className="avm-pf-lead">{leadSentence(g)}</p>
      <p className="avm-pf-gap">Your profile doesn't list “{g.service}”.</p>

      <details className="avm-pf-why">
        <summary>Why we flagged this</summary>

        {single && g.questions[0].outcomes.length > 0 && (
          <>
            <p className="avm-pf-h">What each AI said about you</p>
            <ul className="avm-pf-ul">
              <OutcomeItems outcomes={g.questions[0].outcomes} />
            </ul>
          </>
        )}

        {!single && (
          <>
            <p className="avm-pf-h">Questions you lost, and what each AI said about you</p>
            <ul className="avm-pf-ul">
              {g.questions.map((q, i) => (
                <li key={`${q.question}-${i}`} className="avm-pf-q">
                  <span className="avm-pf-qtext">“{q.question}”</span>
                  {q.outcomes.length > 0 && (
                    <ul className="avm-pf-ul avm-pf-sub">
                      <OutcomeItems outcomes={q.outcomes} />
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}

        {g.citedPages.length > 0 && (
          <>
            <p className="avm-pf-h">{single ? "Pages AI cited for this question" : "Pages AI cited for these questions"}</p>
            <ul className="avm-pf-ul">
              {g.citedPages.map((page) => (
                <li key={page.href}>
                  <a href={page.href} target="_blank" rel="noopener noreferrer" title={page.href}>{page.label}</a>
                </li>
              ))}
            </ul>
          </>
        )}

        {g.checked && (
          <>
            <p className="avm-pf-h">What we checked on your profile</p>
            <ul className="avm-pf-ul">
              <li><span className="avm-pf-muted">Services: </span>{g.checked.services.length ? g.checked.services.join(", ") : "None listed"}</li>
              <li><span className="avm-pf-muted">Primary category: </span>{g.checked.primary || "Not set"}</li>
              <li><span className="avm-pf-muted">Other categories: </span>{g.checked.additional.length ? g.checked.additional.join(", ") : "None"}</li>
            </ul>
          </>
        )}

        {g.expectedChange && (
          <>
            <p className="avm-pf-h">What to change</p>
            <p className="avm-pf-change">{g.expectedChange}</p>
          </>
        )}
      </details>
    </article>
  );
};

/* ─────────────────────────── the part ─────────────────────────────────── */

/**
 * What this part would render, or null for "render nothing". Also tells the
 * dashboard whether an Opportunities card is needed without Search Console.
 */
function content(value: ProfileFindingsBlock | null | undefined): React.ReactNode | null {
  if (!isRecord(value)) return null;
  switch (value.status) {
    case "ok": {
      const raw = Array.isArray(value.findings) ? value.findings : [];
      const findings = raw.map(cleanFinding).filter((f): f is CleanFinding => f !== null);
      if (findings.length) {
        return (
          <div className="avm-pf-list">
            {groupFindings(findings).map((g) => (
              <FindingCard key={serviceKey(g.service)} g={g} />
            ))}
          </div>
        );
      }
      // Findings we cannot read (a code added later, a malformed entry) are
      // not "nothing found": say nothing rather than claim a clean profile.
      if (raw.length) return null;
      const n = typeof value.questions_checked === "number" && value.questions_checked > 0
        ? plural(value.questions_checked, "local question")
        : "the local questions";
      return (
        <p className="avm-pf-line">
          We checked {n} you lost against your Business Profile; no missing services found.
        </p>
      );
    }
    case "not_connected":
    case "needs_reauth":
      // The Google Business Profile section on the same page owns the
      // Connect / Reconnect prompt, from a live read of the connection. A
      // second prompt here would repeat it, and this status is a snapshot from
      // the last check, so it can be stale once the owner has reconnected.
      return null;
    case "audit_failed":
    case "matcher_failed":
      return <p className="avm-pf-line">We couldn't compare your Business Profile this time; the next check will try again.</p>;
    case "insufficient_coverage":
      return (
        <p className="avm-pf-line">
          Too few AI answers came back in this check to compare against your Business Profile; the next check will try again.
        </p>
      );
    case "no_local_lost_questions":
      return <p className="avm-pf-line">No local questions lost in this check.</p>;
    default:
      // not_a_customer, or a status added later: nothing honest to say.
      return null;
  }
}

/** True when the Business Profile part has something to show. */
export function hasProfileFindingsContent(value: ProfileFindingsBlock | null | undefined): boolean {
  return content(value) !== null;
}

const ProfileFindings: React.FC<{ value: ProfileFindingsBlock | null | undefined }> = ({ value }) => {
  const body = content(value);
  if (body === null) return null;
  return (
    <section className="avm-pf-part" aria-label="Business Profile findings">
      <style>{PF_STYLES}</style>
      <p className="avm-eyebrow" style={{ margin: "0 0 10px" }}>From your Google Business Profile</p>
      {body}
    </section>
  );
};

export default ProfileFindings;
